import * as React from 'react';
import {
  Checkbox,
  DetailsList,
  DetailsListLayoutMode,
  ConstrainMode,
  SelectionMode,
  Spinner,
  SpinnerSize,
  Label,
  Stack,
  StackItem,
  Icon,
  IconButton,
  DetailsRow,
  MessageBar,
  MessageBarType,
} from '@fluentui/react';
import type { IDetailsRowProps } from '@fluentui/react';
import { useLocation, useHistory } from 'react-router-dom';
import { Context } from '@micro-frontend-react/core/lib/Context';
import type { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';

import type { IPBTCase } from '../../../Interfaces/IPBTCase';
import { PBTCaseStatus, PBTEngagementModel, PBTDataStatus, PBTFlag } from '../../../Interfaces/IPBTCase';
import { PBTStatusColors, getCaseStatusDisplay } from '../../../Services/PBTConstants';
import { PBTApiEndpoints, PBTMessages } from '../../../Services/PBTConstants';
import { dashboardStyles, detailsListStyles } from './HRCDashboard.styled';
import { useHRCDashboard, updateCaseInStore } from './useHRCDashboard';
import {
  DropdownSearchableClearable,
  OptionType,
} from '../../../Components/DropdownSearchableClearable/DropdownSearchableClearable';
import { CaseDetail } from '../../../Components/PBT/CaseDetail/CaseDetail';
import { SingleValue } from 'react-select';
import { useFeatureChildren, usePermission, useRole } from '../../../Hooks/useFeature';
import { getPaginationItems } from '../../../Utilities/pagination';

export { getPaginationItems } from '../../../Utilities/pagination';

const PAGE_SIZE = 10;
const MANAGER_CHANGED_FLAG = 'MGR';
const CASE_DETAIL_REFRESH_INTERVAL_MS = 30_000;

const statusOptions: OptionType[] = Object.values(PBTCaseStatus).map((s) => ({ value: s, label: s }));
const engModelOptions: OptionType[] = Object.values(PBTEngagementModel).map((m) => ({ value: m, label: m }));
const dataStatusOptions: OptionType[] = Object.values(PBTDataStatus).map((d) => ({ value: d, label: d }));

// Security: mask a value when the caller has read-only/sensitive access to its column.
function maskValue(v: unknown): string {
  const s = v == null ? '' : String(v);
  return s ? '••••••' : '—';
}

function getCaseFlags(item: IPBTCase): string[] {
  const flags: string[] = [...(item.flags || [])];
  if (item.gerWitCheckStatus && !flags.includes(PBTFlag.GERWIT)) {
    flags.push(PBTFlag.GERWIT);
  }
  if (item.isManagerChanged) {
    flags.push(MANAGER_CHANGED_FLAG);
  }
  const reviewedFlags = flags.filter(
    (flag) =>
      (flag === PBTFlag.LOA && item.hrcLoaReviewed)
      || (flag === PBTFlag.GERWIT && item.hrcGerWitReviewed)
      || (flag === MANAGER_CHANGED_FLAG && item.isManagerChangeReviewed)
  );
  const unreviewedFlags = flags.filter((flag) => !reviewedFlags.includes(flag));

  return [
    ...(reviewedFlags.length > 0 ? [`${reviewedFlags.join(', ')} (Reviewed)`] : []),
    ...unreviewedFlags,
  ];
}

function formatDashboardDate(raw?: string | null): string {
  if (!raw) return '';
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (iso) return `${iso[1]}/${iso[2]}/${iso[3]}`;
  const d = new Date(raw);
  if (!isNaN(d.getTime())) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}/${mm}/${dd}`;
  }
  return raw;
}

// Maps DetailsList column keys to their capability key under page.hrcDashboard/table.cases.
const COLUMN_CAPABILITY: Record<string, string> = {
  empName: 'col.case.empName',
  empAlias: 'col.case.empName',
  pern: 'col.case.pern',
  crmCase: 'col.case.crm',
  status: 'col.case.status',
  engModel: 'col.case.engModel',
  manager: 'col.case.manager',
  managerAlias: 'col.case.managerAlias',
  hrcName: 'col.case.hrcName',
  termConvDate: 'col.case.termConvDate',
  effTermDate: 'col.case.effTermDate',
  daysInStatus: 'col.case.daysInStatus',
  flags: 'col.case.flags',
};

const HRCDashboard = (): React.ReactElement => {
  const {
    casesState,
    allCases,
    filteredCases,
    setSearchText,
    statusFilter,
    setStatusFilter,
    engModelFilter,
    setEngModelFilter,
    dataStatusFilter,
    setDataStatusFilter,
    managerFilter,
    setManagerFilter,
    crmCaseFilter,
    setCrmCaseFilter,
    termConvDateFilter,
    setTermConvDateFilter,
    effTermDateFilter,
    setEffTermDateFilter,
    showFlaggedOnly,
    setShowFlaggedOnly,
    filterOptions,
    stats,
    flaggedCount,
    activeStatCard,
    handleStatCardClick,
    handleRowClick,
    refreshCases,
    refreshCase,
    pageNumber,
    totalCount,
    totalPages,
    setPageNumber,
  } = useHRCDashboard();

  // Add Case is an ACTION → gate by the Cases.Create permission (matches the API), not the tree.
  const canAddCase = usePermission('Cases.Create');
  // Managers get a personalized "My Dashboard" label (nav + title); everyone else sees "HRC Dashboard".
  const isManager = useRole() === 'Manager';
  const colFeatures = useFeatureChildren('page.hrcDashboard/table.cases');
  const maskedCapKeys = React.useMemo(() => {
    const s = new Set<string>();
    colFeatures.forEach((c) => {
      if (c.node.accessLevel === 1 || c.node.isSensitive) s.add(c.key);
    });
    return s;
  }, [colFeatures]);

  React.useEffect(() => {
    if (isManager && engModelFilter) {
      setEngModelFilter('');
      setInputEngModel('');
    }
  }, [isManager, engModelFilter, setEngModelFilter]);

  const location = useLocation<{ successMessage?: string }>();
  const history = useHistory();
  const { authClient } = React.useContext(Context as React.Context<IEmployeeExperienceContext>);
  const [toastMessage, setToastMessage] = React.useState<string | null>(null);
  const [isExporting, setIsExporting] = React.useState(false);

  const handleExportExcel = React.useCallback(async () => {
    setIsExporting(true);
    try {
      const token = await authClient.acquireToken(__SCOPES__);
      const response = await fetch(PBTApiEndpoints.ExportCases, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error(`API returned ${response.status}`);

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition') || '';
      const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const fileName = match
        ? decodeURIComponent(match[1])
        : `PBT_Cases_${new Date().toISOString().slice(0, 10)}.xlsx`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setToastMessage(PBTMessages.ExportSuccess);
    } catch {
      setToastMessage(PBTMessages.ExportError);
    } finally {
      setIsExporting(false);
    }
  }, [authClient]);

  React.useEffect(() => {
    if (location.state?.successMessage) {
      const msg = location.state.successMessage;
      history.replace({ ...location, state: {} });
      setToastMessage(msg);
      refreshCases();
    }
  }, [location.state?.successMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const handleRefresh = () => refreshCases();
    window.addEventListener('pbt:refresh-cases', handleRefresh);
    return () => window.removeEventListener('pbt:refresh-cases', handleRefresh);
  }, [refreshCases]);

  // Auto-dismiss toast after 5 seconds
  React.useEffect(() => {
    if (!toastMessage) return;
    const timer = setTimeout(() => setToastMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  // Column filter values/setters come from the dashboard hook (all server-side now).

  // Case detail panel
  const [selectedCase, setSelectedCase] = React.useState<IPBTCase | null>(null);
  const [caseRefreshError, setCaseRefreshError] = React.useState(false);
  const caseDetailRef = React.useRef<HTMLDivElement>(null);

  const onRowClick = React.useCallback(
    (item: IPBTCase) => {
      if (selectedCase?.id === item.id) {
        setSelectedCase(null);
        return;
      }
      setSelectedCase(item);
      handleRowClick(item);
    },
    [handleRowClick, selectedCase]
  );

  React.useEffect(() => {
    const caseId = selectedCase?.id;
    if (!caseId) {
      setCaseRefreshError(false);
      return;
    }

    let disposed = false;
    let inFlight = false;
    let controller: AbortController | null = null;

    const refreshSelectedCase = async () => {
      if (inFlight) return;
      inFlight = true;
      controller = new AbortController();
      try {
        const freshCase = await refreshCase(caseId, controller.signal);
        if (!disposed) {
          setSelectedCase((current) => (current?.id === caseId ? freshCase : current));
          setCaseRefreshError(false);
        }
      } catch (error) {
        if (!disposed && (error as Error).name !== 'AbortError') {
          setCaseRefreshError(true);
        }
      } finally {
        inFlight = false;
        controller = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refreshSelectedCase();
      }
    };

    void refreshSelectedCase();
    const intervalId = window.setInterval(refreshSelectedCase, CASE_DETAIL_REFRESH_INTERVAL_MS);
    window.addEventListener('focus', refreshSelectedCase);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disposed = true;
      controller?.abort();
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshSelectedCase);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshCase, selectedCase?.id]);

  // Highlight the currently selected row
  const onRenderRow = React.useCallback(
    (props?: IDetailsRowProps) => {
      if (!props) return null;
      const item = props.item as IPBTCase;
      const isSelected = selectedCase?.id === item.id;
      const employeeNameCapability = COLUMN_CAPABILITY.empName;
      const canExposeEmployeeName =
        !employeeNameCapability || !maskedCapKeys.has(employeeNameCapability);
      return (
        <DetailsRow
          {...props}
          getRowAriaLabel={() =>
            canExposeEmployeeName && item.empName
              ? `Open case for ${item.empName}`
              : 'Open case details'
          }
          focusZoneProps={{
            tabIndex: 0,
            onClick: () => onRowClick(item),
            onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onRowClick(item);
              }
            },
          }}
          styles={{
            root: {
              backgroundColor: isSelected ? '#e6f2ff' : undefined,
              fontWeight: isSelected ? 600 : undefined,
              cursor: 'pointer',
            },
          }}
        />
      );
    },
    [maskedCapKeys, onRowClick, selectedCase]
  );

  // Scroll to CaseDetail when a case is selected. Key this on the case *id* rather than the
  // selectedCase object reference: a background case-list refresh (pbt:refresh-cases, or an
  // updateCaseInStore patch) replaces the selected case with a fresh object of the SAME id via
  // the [allCases] sync effect below. Depending on the object reference re-fired this scroll on
  // every such refresh, yanking the viewport back down to the panel even though the user had not
  // changed their selection. Keying on the id scrolls only on an actual (re)selection.
  React.useEffect(() => {
    if (selectedCase && caseDetailRef.current) {
      setTimeout(() => {
        caseDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [selectedCase?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the open detail panel in sync after a refresh (e.g. after Proceed releases a case).
  // refreshCases() reloads the shared list but does not touch the panel's selected snapshot, so
  // re-select the matching case from the freshly fetched data to reflect its new status/stepper.
  React.useEffect(() => {
    setSelectedCase((prev) => {
      if (!prev) return prev;
      const fresh = allCases.find((c) => c.id === prev.id);
      return fresh ?? prev;
    });
  }, [allCases]);

  // Input values for searchable dropdowns
  const [inputEmployee, setInputEmployee] = React.useState('');
  const [inputStatus, setInputStatus] = React.useState('');
  const [inputEngModel, setInputEngModel] = React.useState('');
  const [inputManager, setInputManager] = React.useState('');
  const [inputCrmCase, setInputCrmCase] = React.useState('');
  const [inputTermConvDate, setInputTermConvDate] = React.useState('');
  const [inputEffTermDate, setInputEffTermDate] = React.useState('');
  const [inputDataStatus, setInputDataStatus] = React.useState('');
  const [selectedEmployee, setSelectedEmployee] = React.useState<OptionType | null>(null);

  React.useEffect(() => {
    if (selectedEmployee) return;

    const term = inputEmployee.trim();
    const timer = window.setTimeout(() => {
      // Keep the server-side search in sync with the visible input:
      //   empty      -> clear search
      //   1-2 chars  -> clear search (don't keep filtering by a stale longer term, e.g. "John" -> "Jo")
      //   3+ chars   -> search
      setSearchText(term.length === 0 || term.length >= 3 ? term : '');
    }, 300);

    return () => window.clearTimeout(timer);
  }, [inputEmployee, selectedEmployee, setSearchText]);

  const managerOptions: OptionType[] = React.useMemo(
    () => filterOptions.managers.map((manager) => ({ label: manager.name, value: manager.alias })),
    [filterOptions.managers]
  );

  const crmCaseOptions: OptionType[] = React.useMemo(
    () => (filterOptions.crmCases || []).map((m) => ({ value: m, label: m })),
    [filterOptions.crmCases]
  );

  const termConvDateOptions: OptionType[] = React.useMemo(
    () => (filterOptions.termConvDates || []).map((m) => ({ value: m, label: formatDashboardDate(m) })),
    [filterOptions.termConvDates]
  );

  const effTermDateOptions: OptionType[] = React.useMemo(
    () => (filterOptions.effTermDates || []).map((m) => ({ value: m, label: formatDashboardDate(m) })),
    [filterOptions.effTermDates]
  );

  const employeeOptions: OptionType[] = React.useMemo(() => {
    const seen: Record<string, boolean> = {};
    const results: OptionType[] = [];
    for (const c of allCases) {
      const key = c.pern || c.empName;
      if (!key || seen[key]) continue;
      seen[key] = true;
      const label = c.empName && c.pern ? `${c.empName} (${c.pern})` : c.empName || c.pern;
      results.push({ value: key, label });
    }
    return results.sort((a, b) => a.label.localeCompare(b.label));
  }, [allCases]);

  // All filtering is server-side; the loaded page is already the filtered result set.
  const displayedCases = filteredCases;

  // Close the detail panel when the selected case is no longer on the loaded page.
  React.useEffect(() => {
    setSelectedCase((prev) => {
      if (!prev) return prev;
      return displayedCases.some((c) => c.id === prev.id) ? prev : null;
    });
  }, [displayedCases]);

  const currentPage = Math.max(0, pageNumber - 1);
  const paginatedCases = displayedCases;

  const onPageChange = React.useCallback((index: number) => {
    setPageNumber(index + 1);
  }, [setPageNumber]);

  const baseColumns = React.useMemo(
    () => [
      { key: 'empName', name: 'Employee Name', fieldName: 'empName', minWidth: 140, maxWidth: 180, isResizable: true },
      {
        key: 'empAlias',
        name: 'Employee Alias',
        fieldName: 'empAlias',
        minWidth: 100,
        maxWidth: 130,
        isResizable: true,
        onRender: (item: IPBTCase) => <span>{item.empAlias || '—'}</span>,
      },
      { key: 'pern', name: 'PERN', fieldName: 'pern', minWidth: 70, maxWidth: 90, isResizable: true },
      { key: 'crmCase', name: 'CRM Case #', fieldName: 'crmCase', minWidth: 120, maxWidth: 150, isResizable: true },
      {
        key: 'flags',
        name: 'Flags',
        fieldName: 'flags',
        minWidth: 120,
        maxWidth: 160,
        isResizable: true,
        onRender: (item: IPBTCase) => {
          const flags = getCaseFlags(item);
          return <span>{flags.length > 0 ? flags.join(', ') : '—'}</span>;
        },
      },
      {
        key: 'status',
        name: 'Case Status',
        fieldName: 'status',
        minWidth: 120,
        maxWidth: 160,
        isResizable: true,
        onRender: (item: IPBTCase) => {
          const defaultColors = { background: '#f3f2f1', color: '#323130' };
          const display = getCaseStatusDisplay(item.status, item.emailFailed);
          const colors = PBTStatusColors[display.colorKey as PBTCaseStatus] || defaultColors;
          return (
            <span
              title={display.tooltip}
              style={{
                backgroundColor: colors.background,
                color: colors.color,
                padding: '4px 10px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              {display.label}
            </span>
          );
        },
      },
      {
        key: 'engModel',
        name: 'Engagement Model',
        fieldName: 'engModel',
        minWidth: 160,
        maxWidth: 200,
        isResizable: true,
      },
      { key: 'manager', name: 'Manager', fieldName: 'manager', minWidth: 120, maxWidth: 160, isResizable: true },
      {
        key: 'managerAlias',
        name: 'Manager Alias',
        fieldName: 'managerAlias',
        minWidth: 100,
        maxWidth: 130,
        isResizable: true,
        onRender: (item: IPBTCase) => <span>{item.managerAlias || '—'}</span>,
      },
      {
        key: 'hrcName',
        name: 'HRC Name',
        fieldName: 'hrcName',
        minWidth: 120,
        maxWidth: 160,
        isResizable: true,
        onRender: (item: IPBTCase) => <span>{item.hrcName || '—'}</span>,
      },
      {
        key: 'termConvDate',
        name: 'Term Conv Date',
        fieldName: 'termConvDate',
        minWidth: 110,
        maxWidth: 130,
        isResizable: true,
        onRender: (item: IPBTCase) => <span>{formatDashboardDate(item.termConvDate) || '—'}</span>,
      },
      {
        key: 'effTermDate',
        name: 'Eff. Term Date',
        fieldName: 'effTermDate',
        minWidth: 110,
        maxWidth: 130,
        isResizable: true,
        onRender: (item: IPBTCase) => <span>{formatDashboardDate(item.effTermDate) || '—'}</span>,
      },
      {
        key: 'daysInStatus',
        name: 'Days in Status',
        fieldName: 'daysInStatus',
        minWidth: 90,
        maxWidth: 110,
        isResizable: true,
      },
      {
        key: 'dataStatus',
        name: 'Data Status',
        fieldName: 'dataStatus',
        minWidth: 110,
        maxWidth: 140,
        isResizable: true,
        onRender: (item: IPBTCase) => {
          const status = item.dataStatus;
          if (!status) {
            return (
              <span
                style={{ color: '#605e5c', backgroundColor: '#f3f2f1', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}
              >
                Unavailable
              </span>
            );
          }
          const color = status === 'Blocked' ? '#a80000' : status === 'Action Needed' ? '#6b5700' : '#0b6a0b';
          const bg = status === 'Blocked' ? '#fde7e9' : status === 'Action Needed' ? '#fff4ce' : '#e6f7e6';
          return (
            <span
              style={{ color, backgroundColor: bg, padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}
            >
              {status}
            </span>
          );
        },
      },
    ],
    []
  );

  // Security overlay: mask columns the caller has read-only/sensitive access to (from the feature tree).
  // Employee Alias is visible to every role. Manager "My Dashboard" hides internal HRC workflow metadata.
  const columns = React.useMemo(
    () =>
      baseColumns
        .filter((col) => !(isManager && (col.key === 'dataStatus' || col.key === 'flags' || col.key === 'engModel')))
        .map((col) => {
          const capKey = COLUMN_CAPABILITY[col.key];
          if (capKey && maskedCapKeys.has(capKey)) {
            return {
              ...col,
              onRender: (item: IPBTCase) => <span>{maskValue(item[col.fieldName as keyof IPBTCase])}</span>,
            };
          }
          return col;
        }),
    [baseColumns, maskedCapKeys, isManager]
  );

  return (
    <main role="main" className={dashboardStyles.container}>
      <div className={dashboardStyles.titleRow}>
        <h1 className={dashboardStyles.pageTitle}>{isManager ? 'My Dashboard' : 'HRC Dashboard'}</h1>
        <div className={dashboardStyles.titleActions}>
          <button
            type="button"
            className={dashboardStyles.refreshButton}
            onClick={refreshCases}
            disabled={casesState.isLoading}
            aria-label="Refresh dashboard data"
          >
            <Icon iconName="Refresh" aria-hidden="true" />
            {casesState.isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
          {canAddCase && (
            <button
              type="button"
              className={dashboardStyles.addCaseButton}
              onClick={() => history.push('/PBT/CRMCases')}
            >
              + Add new PBT case
            </button>
          )}
          <button
            type="button"
            className={dashboardStyles.addCaseButton}
            onClick={handleExportExcel}
            disabled={isExporting || casesState.isLoading}
            aria-label="Export all case data to Excel"
          >
            <Icon iconName="ExcelDocument" aria-hidden="true" styles={{ root: { marginRight: 6 } }} />
            {isExporting ? 'Exporting...' : 'Export to Excel'}
          </button>
        </div>
      </div>

      {/* Managers get a streamlined "My Dashboard" (own Date & Attestation Pending cases only), so the
          summary stat cards are hidden for the Manager role. */}
      {!isManager && (
        <div className={dashboardStyles.statCardsRow} role="group" aria-label="Case statistics">
          <div
            className={`${dashboardStyles.statCard} ${
              activeStatCard === 'totalActive' ? dashboardStyles.statCardActive : ''
            }`}
            onClick={() => handleStatCardClick('totalActive')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleStatCardClick('totalActive');
              }
            }}
            tabIndex={0}
            role="button"
            aria-pressed={activeStatCard === 'totalActive'}
            style={{ borderTopColor: '#0b6a0b' }}
          >
            <span className={dashboardStyles.statNumber} style={{ color: '#0b6a0b' }}>
              {stats.totalActive}
            </span>
            <span className={dashboardStyles.statLabel}>TOTAL ACTIVE</span>
          </div>
          <div
            className={`${dashboardStyles.statCard} ${
              activeStatCard === 'dataGathering' ? dashboardStyles.statCardActive : ''
            }`}
            onClick={() => handleStatCardClick('dataGathering')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleStatCardClick('dataGathering');
              }
            }}
            tabIndex={0}
            role="button"
            aria-pressed={activeStatCard === 'dataGathering'}
            style={{ borderTopColor: '#0078d4' }}
          >
            <span className={dashboardStyles.statNumber} style={{ color: '#0078d4' }}>
              {stats.dataGathering}
            </span>
            <span className={dashboardStyles.statLabel}>DATA GATHERING</span>
          </div>
          <div
            className={`${dashboardStyles.statCard} ${
              activeStatCard === 'awaiting' ? dashboardStyles.statCardActive : ''
            }`}
            onClick={() => handleStatCardClick('awaiting')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleStatCardClick('awaiting');
              }
            }}
            tabIndex={0}
            role="button"
            aria-pressed={activeStatCard === 'awaiting'}
            style={{ borderTopColor: '#8764b8' }}
          >
            <span className={dashboardStyles.statNumber} style={{ color: '#8764b8' }}>
              {stats.awaiting}
            </span>
            <span className={dashboardStyles.statLabel}>DATE &amp; ATTESTATION PENDING</span>
          </div>
          <div
            className={`${dashboardStyles.statCard} ${
              activeStatCard === 'complete' ? dashboardStyles.statCardActive : ''
            }`}
            onClick={() => handleStatCardClick('complete')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleStatCardClick('complete');
              }
            }}
            tabIndex={0}
            role="button"
            aria-pressed={activeStatCard === 'complete'}
            style={{ borderTopColor: '#107c10' }}
          >
            <span className={dashboardStyles.statNumber} style={{ color: '#107c10' }}>
              {stats.complete}
            </span>
            <span className={dashboardStyles.statLabel}>COMPLETE</span>
          </div>
        </div>
      )}

      {!isManager && flaggedCount > 0 && (
        <div className={dashboardStyles.warningBanner}>
          <Icon iconName="Warning" styles={{ root: { color: '#d83b01', marginRight: 6 } }} /> {flaggedCount} case
          {flaggedCount > 1 ? 's' : ''} require attention — review flagged items below.
        </div>
      )}

      <Stack horizontal className={dashboardStyles.filtersRow} wrap>
        <StackItem className={dashboardStyles.filterItem}>
          <Label className={dashboardStyles.filterLabel}>Employee/PERN</Label>
          <DropdownSearchableClearable
            placeholder="Employee"
            name="employee"
            options={employeeOptions}
            value={selectedEmployee}
            inputValue={inputEmployee}
            setInputValue={(value) => {
              setSelectedEmployee(null);
              setInputEmployee(value);
            }}
            onChange={(option: SingleValue<OptionType>) => {
              setSelectedEmployee(option);
              setSearchText(option ? option.value : '');
              setInputEmployee('');
            }}
            filterMinLength={3}
            ariaLabel="Employee/PERN filter"
          />
        </StackItem>
        <StackItem className={dashboardStyles.filterItem}>
          <Label className={dashboardStyles.filterLabel}>CRM Case #</Label>
          <DropdownSearchableClearable
            placeholder="CRM Case #"
            name="crmCase"
            options={crmCaseOptions}
            value={crmCaseFilter ? { value: crmCaseFilter, label: crmCaseFilter } : null}
            inputValue={inputCrmCase}
            setInputValue={setInputCrmCase}
            onChange={(option: SingleValue<OptionType>) => {
              setCrmCaseFilter(option ? option.value : '');
              setInputCrmCase('');
            }}
            filterMinLength={0}
            ariaLabel="CRM Case filter"
          />
        </StackItem>
        <StackItem className={dashboardStyles.filterItem}>
          <Label className={dashboardStyles.filterLabel}>Case Status</Label>
          <DropdownSearchableClearable
            placeholder="Status"
            name="status"
            options={statusOptions}
            value={statusFilter ? { value: statusFilter, label: statusFilter } : null}
            inputValue={inputStatus}
            setInputValue={setInputStatus}
            onChange={(option: SingleValue<OptionType>) => {
              setStatusFilter(option ? option.value : '');
              setInputStatus('');
            }}
            filterMinLength={0}
            ariaLabel="Case Status filter"
          />
        </StackItem>
        {!isManager && (
          <StackItem className={dashboardStyles.filterItem}>
            <Label className={dashboardStyles.filterLabel}>Engagement Model</Label>
            <DropdownSearchableClearable
              placeholder="Engagement Model"
              name="engModel"
              options={engModelOptions}
              value={engModelFilter ? { value: engModelFilter, label: engModelFilter } : null}
              inputValue={inputEngModel}
              setInputValue={setInputEngModel}
              onChange={(option: SingleValue<OptionType>) => {
                setEngModelFilter(option ? option.value : '');
                setInputEngModel('');
              }}
              filterMinLength={0}
              ariaLabel="Engagement Model filter"
            />
          </StackItem>
        )}
        <StackItem className={dashboardStyles.filterItem}>
          <Label className={dashboardStyles.filterLabel}>Manager</Label>
          <DropdownSearchableClearable
            placeholder="Manager"
            name="manager"
            options={managerOptions}
            value={managerFilter}
            inputValue={inputManager}
            setInputValue={setInputManager}
            onChange={(option: SingleValue<OptionType>) => {
              setManagerFilter(option);
              setInputManager('');
            }}
            filterMinLength={0}
            ariaLabel="Manager filter"
          />
        </StackItem>
        <StackItem className={dashboardStyles.filterItem}>
          <Label className={dashboardStyles.filterLabel}>Term Conv Date</Label>
          <DropdownSearchableClearable
            placeholder="Term Conv Date"
            name="termConvDate"
            options={termConvDateOptions}
            value={
              termConvDateFilter ? { value: termConvDateFilter, label: formatDashboardDate(termConvDateFilter) } : null
            }
            inputValue={inputTermConvDate}
            setInputValue={setInputTermConvDate}
            onChange={(option: SingleValue<OptionType>) => {
              setTermConvDateFilter(option ? option.value : '');
              setInputTermConvDate('');
            }}
            filterMinLength={0}
            ariaLabel="Term Conv Date filter"
          />
        </StackItem>
        <StackItem className={dashboardStyles.filterItem}>
          <Label className={dashboardStyles.filterLabel}>Eff. Term Date</Label>
          <DropdownSearchableClearable
            placeholder="Eff. Term Date"
            name="effTermDate"
            options={effTermDateOptions}
            value={
              effTermDateFilter ? { value: effTermDateFilter, label: formatDashboardDate(effTermDateFilter) } : null
            }
            inputValue={inputEffTermDate}
            setInputValue={setInputEffTermDate}
            onChange={(option: SingleValue<OptionType>) => {
              setEffTermDateFilter(option ? option.value : '');
              setInputEffTermDate('');
            }}
            filterMinLength={0}
            ariaLabel="Eff. Term Date filter"
          />
        </StackItem>
        {!isManager && (
        <StackItem className={dashboardStyles.filterItem}>
          <Label className={dashboardStyles.filterLabel}>Data Status</Label>
          <DropdownSearchableClearable
            placeholder="Data Status"
            name="dataStatus"
            options={dataStatusOptions}
            value={dataStatusFilter ? { value: dataStatusFilter, label: dataStatusFilter } : null}
            inputValue={inputDataStatus}
            setInputValue={setInputDataStatus}
            onChange={(option: SingleValue<OptionType>) => {
              setDataStatusFilter(option ? option.value : '');
              setInputDataStatus('');
            }}
            filterMinLength={0}
            ariaLabel="Data Status filter"
          />
        </StackItem>
        )}
        {!isManager && (
        <StackItem className={dashboardStyles.filterItemCheckbox}>
          <Label className={dashboardStyles.filterLabel}>&nbsp;</Label>
          <Checkbox label="Flagged only" checked={showFlaggedOnly} onChange={(_, checked) => setShowFlaggedOnly(!!checked)} />
        </StackItem>
        )}
      </Stack>

      {casesState.hasError && !casesState.isLoading && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          actions={
            <IconButton
              iconProps={{ iconName: 'Refresh' }}
              title="Retry loading cases"
              ariaLabel="Retry loading cases"
              onClick={refreshCases}
            />
          }
        >
          {casesState.errorMessage || 'Unable to load PBT cases.'}
        </MessageBar>
      )}

      <span className={dashboardStyles.countText}>
        Showing {displayedCases.length} of {totalCount} cases
      </span>

      {casesState.isLoading && allCases.length === 0 ? (
        <div className={dashboardStyles.spinnerContainer}>
          <Spinner size={SpinnerSize.large} label="Loading cases..." />
        </div>
      ) : (
        <div className={dashboardStyles.gridContainer}>
          <DetailsList
            key={`grid-${selectedCase?.id ?? 'none'}`}
            items={paginatedCases}
            columns={columns}
            layoutMode={DetailsListLayoutMode.fixedColumns}
            constrainMode={ConstrainMode.unconstrained}
            selectionMode={SelectionMode.none}
            onRenderRow={onRenderRow}
            styles={detailsListStyles}
          />
          {/* Custom Pagination */}
          {(() => {
            const totalItems = totalCount;
            if (totalItems === 0) {
              return (
                <div
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 0', gap: 4 }}
                >
                  <span style={{ fontSize: 12, color: '#505050' }}>0 of 0</span>
                </div>
              );
            }
            const pageCount = Math.max(totalPages, 1);
            const startItem = currentPage * PAGE_SIZE + 1;
            const endItem = Math.min((currentPage + 1) * PAGE_SIZE, totalItems);
            return (
              <div
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 0', gap: 4 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <IconButton
                    iconProps={{ iconName: 'DoubleChevronLeft' }}
                    title="First page"
                    ariaLabel="first page"
                    disabled={currentPage === 0}
                    onClick={() => onPageChange(0)}
                    styles={{ root: { height: 28, width: 28 }, icon: { fontSize: 12 } }}
                  />
                  <IconButton
                    iconProps={{ iconName: 'ChevronLeft' }}
                    title="Previous page"
                    ariaLabel="previous page"
                    disabled={currentPage === 0}
                    onClick={() => onPageChange(currentPage - 1)}
                    styles={{ root: { height: 28, width: 28 }, icon: { fontSize: 12 } }}
                  />
                  {getPaginationItems(currentPage, pageCount).map((item, idx) =>
                    item === 'ellipsis' ? (
                      <span
                        key={`ellipsis-${idx}`}
                        aria-hidden="true"
                        style={{
                          minWidth: 20,
                          height: 28,
                          lineHeight: '28px',
                          fontSize: 14,
                          color: '#605e5c',
                          textAlign: 'center',
                          userSelect: 'none',
                        }}
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={item}
                        type="button"
                        onClick={() => onPageChange(item)}
                        aria-label={`page ${item + 1}${item === currentPage ? ' selected' : ''}`}
                        style={{
                          minWidth: 28,
                          height: 28,
                          lineHeight: '28px',
                          border: 'none',
                          borderRadius: 3,
                          cursor: 'pointer',
                          fontSize: 14,
                          fontWeight: item === currentPage ? 700 : 400,
                          backgroundColor: item === currentPage ? '#0078d4' : 'transparent',
                          color: item === currentPage ? '#fff' : '#323130',
                          padding: '0 6px',
                          textAlign: 'center',
                        }}
                      >
                        {item + 1}
                      </button>
                    )
                  )}
                  <IconButton
                    iconProps={{ iconName: 'ChevronRight' }}
                    title="Next page"
                    ariaLabel="next page"
                    disabled={currentPage >= pageCount - 1}
                    onClick={() => onPageChange(currentPage + 1)}
                    styles={{ root: { height: 28, width: 28 }, icon: { fontSize: 12 } }}
                  />
                  <IconButton
                    iconProps={{ iconName: 'DoubleChevronRight' }}
                    title="Last page"
                    ariaLabel="last page"
                    disabled={currentPage >= pageCount - 1}
                    onClick={() => onPageChange(pageCount - 1)}
                    styles={{ root: { height: 28, width: 28 }, icon: { fontSize: 12 } }}
                  />
                </div>
                <span style={{ fontSize: 12, color: '#505050' }}>
                  {startItem} - {endItem} of {totalItems}
                </span>
              </div>
            );
          })()}
        </div>
      )}

      {selectedCase && (
        <div ref={caseDetailRef}>
          {caseRefreshError && (
            <MessageBar messageBarType={MessageBarType.warning}>
              {PBTMessages.CaseRefreshError}
            </MessageBar>
          )}
          <CaseDetail
            selectedCase={selectedCase}
            onClose={() => setSelectedCase(null)}
            onCaseUpdated={() => {
              updateCaseInStore(selectedCase.id, { engModel: PBTEngagementModel.HRCLed });
              setSelectedCase((prev) => (prev ? { ...prev, engModel: PBTEngagementModel.HRCLed } : null));
              refreshCases();
            }}
            onCaseReleased={() => refreshCases()}
          />
        </div>
      )}

      {toastMessage && (() => {
        const isErrorToast = toastMessage === PBTMessages.ExportError;
        return (
          <div
            role="alert"
            style={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 9999,
              backgroundColor: isErrorToast ? '#a80000' : '#1b5e20',
              color: '#fff',
              padding: '12px 20px',
              borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              maxWidth: 420,
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            <Icon iconName={isErrorToast ? 'StatusErrorFull' : 'CheckMark'} style={{ color: '#fff', fontSize: 16 }} /> {toastMessage}
          </div>
        );
      })()}
    </main>
  );
};

const HRCDashboardMemo = React.memo(HRCDashboard);
export default HRCDashboardMemo;
export { HRCDashboardMemo as HRCDashboard };
